import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  MODAL_TYPE,
  BACK_IN_STOCK_BUTTON_ID,
  BACK_IN_STOCK_SELECTED_PRODUCT,
  BACK_IN_STOCK_SELECTED_VARIANT,
} from "../../constants";
import Modal from "../modal/Modal";
import Button from "../buttons/button/Button";
import useEventBus from "../../hooks/useEventBus";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useViewport } from "../../context/viewport/ViewportContext";
import {
  getAnonymousId,
  anonymousAddVariantToNotifications,
} from "../../api/backInStockApi";
import {
  AnonymousBackInStockModalParameters,
  AnonymousBackInStockModalCopy,
} from "../../types/components/anonymousBackInStock";
import replaceJSX from "./utils/replaceJSX";
import AnonymousBackInStockRecognisedCustomer from "./AnonymousBackInStockRecognisedCustomer";

import styles from "./AnonymousBackInStockModal.scss";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";
import { checkEmailStatus } from "./utils/checkEmailStatus";
import validateEmail from "./utils/validateEmail";
import getWindow from "../../utils/window";
import { loginRedirect } from "../../utils/identity";
import {
  ProgressIndicator,
  SIZE_SMALL,
} from "@asosteam/asos-web-component-library-progress-indicator";
import { createAnonymousBrazeUser } from "./utils/createAnonymousBrazeUser";
import { setSessionStorageItem } from "../../utils/storage";
import { useAnonymousBackInStock } from "../../context/anonymousBackInStock/AnonymousBackInStockContext";
import { ERROR_TYPE, logError } from "../../utils/newRelicLogger";
import {
  fireRegisteredAccountScreenLoadAnalytics,
  fireAnonymousBackInStockSuccessAnalytics,
} from "../../analytics/anonymousBackInStock";
import { getFeature, track } from "../../utils/features";
import { useFeaturesReady } from "../../context/featuresReady/FeaturesReadyContext";

const AnonymousBackInStockModal = (): JSX.Element | null => {
  const recognisedHeadingRef = useRef<HTMLHeadingElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const wasEmailRegistered = useRef(false);
  const [modalParameters, setModalParameters] =
    useState<AnonymousBackInStockModalParameters | null>(null);
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [emailIsRegistered, setEmailIsRegistered] = useState(false);
  const { getTranslation } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
  };
  const [loading, setLoading] = useState(false);
  const { viewportCategory } = useViewport()! as {
    viewportCategory: string;
  };
  const { saveVariant } = useAnonymousBackInStock()!;
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };

  const closeModal = useCallback(() => {
    setEmailIsRegistered(false);
    setModalParameters(null);
    setEmail("");
    setEmailError(false);
    setLoading(false);

    if (modalParameters?.focusOnCloseElement) {
      modalParameters.focusOnCloseElement.focus();
    }
  }, [modalParameters]);

  useEffect(() => {
    if (emailIsRegistered) {
      recognisedHeadingRef.current?.focus();
    } else if (wasEmailRegistered.current && modalParameters) {
      emailInputRef.current?.focus();
    }
    wasEmailRegistered.current = emailIsRegistered;
  }, [emailIsRegistered, modalParameters]);

  useEffect(() => {
    if (emailIsRegistered) {
      fireRegisteredAccountScreenLoadAnalytics(
        modalParameters?.selectedVariantId
      );
    }
  }, [emailIsRegistered, modalParameters]);

  const handleError = useCallback(() => {
    closeModal();
    getWindow().asos?.eventBus?.emit("notificationBar", {
      type: "error",
      copy: getTranslation("anonymous_back_in_stock_modal_generic_error"),
      refocusId: BACK_IN_STOCK_BUTTON_ID,
    });
    setEmail("");
  }, [getTranslation, closeModal]);

  const setBackInStockSessionStorage = useCallback(() => {
    if (modalParameters?.selectedProductId) {
      setSessionStorageItem(
        BACK_IN_STOCK_SELECTED_PRODUCT,
        modalParameters.selectedProductId
      );
    }
    if (modalParameters?.selectedVariantId) {
      setSessionStorageItem(
        BACK_IN_STOCK_SELECTED_VARIANT,
        modalParameters.selectedVariantId
      );
    }
  }, [modalParameters]);

  const trackBisMetric = useCallback(() => {
    if (areFeaturesReady) {
      getFeature("pdp_anonymous_backinstock_enabled")?.isEnabled();
      track("back_in_stock_subscribe");
    }
  }, [areFeaturesReady]);

  const handleAnonymousBackInStock = useCallback(async () => {
    if (!modalParameters?.selectedVariantId) {
      setLoading(false);
      handleError();
      return;
    }

    try {
      const trimmedEmail = email.trim();
      const anonymousId = await getAnonymousId(trimmedEmail);
      await createAnonymousBrazeUser({
        email: trimmedEmail,
        anonymousId,
        awaitAuthResult: true,
      });
      await anonymousAddVariantToNotifications({
        anonymousId,
        variantId: modalParameters.selectedVariantId,
      });
      saveVariant(modalParameters.selectedVariantId);
      closeModal();
      getWindow().asos?.eventBus?.emit("notificationBar", {
        type: "success",
        copy: getTranslation("back_in_stock_confirmation"),
        refocusId: BACK_IN_STOCK_BUTTON_ID,
      });
      fireAnonymousBackInStockSuccessAnalytics(
        modalParameters.selectedVariantId
      ).catch((analyticsError: Error) =>
        logError(ERROR_TYPE.NonCriticalAppError, {
          action: "anonymousBackInStockAnalytics",
          message: analyticsError.message,
        })
      );
      trackBisMetric();
    } catch (error) {
      handleError();
      logError(ERROR_TYPE.UserActionFailed, {
        action: "anonymousBackInStock",
        message: (error as Error).message,
      });
    } finally {
      setLoading(false);
    }
  }, [
    email,
    modalParameters,
    getTranslation,
    handleError,
    closeModal,
    saveVariant,
    trackBisMetric,
  ]);

  const handleSubmit = useCallback(async () => {
    try {
      const isAvailable = await checkEmailStatus(email);
      if (!isAvailable) {
        setEmailIsRegistered(true);
        setEmail("");
        setEmailError(false);
        setLoading(false);
      } else {
        await handleAnonymousBackInStock();
      }
    } catch {
      handleError();
      setLoading(false);
    }
  }, [email, handleAnonymousBackInStock, handleError]);

  const backInStockSignIn = useCallback(() => {
    if (modalParameters?.selectedVariantId) {
      setBackInStockSessionStorage();
      loginRedirect({
        extraQueryParameters: { ctaRef: "anonymous notify me screen" },
      });
    }
  }, [setBackInStockSessionStorage, modalParameters]);

  const handleNotifyClick = useCallback(async () => {
    setLoading(true);
    if (validateEmail(email)) {
      await handleSubmit();
    } else {
      setEmailError(true);
      setLoading(false);
    }
  }, [email, handleSubmit]);

  useEventBus(
    "anonBisModalOpen",
    (eventData: AnonymousBackInStockModalParameters) => {
      setModalParameters(eventData);
    },
    []
  );

  if (!modalParameters) {
    return null;
  }

  const copy: AnonymousBackInStockModalCopy = {
    notifyMeButtonCopy: getTranslation("anonymous_back_in_stock_modal_cta"),
    signInTitleCopy: getTranslation("anonymous_back_in_stock_modal_heading"),
    signInTextCopy: getTranslation("anonymous_back_in_stock_modal_body"),
  };

  const { focusOnCloseElement } = modalParameters;

  const { signInTitleCopy, signInTextCopy, notifyMeButtonCopy } = copy;
  return (
    <Modal
      contentLoaded={true}
      modalType={
        viewportCategory === "small-viewport"
          ? MODAL_TYPE.BOTTOM
          : MODAL_TYPE.CENTER
      }
      open
      closeModal={closeModal}
      modalId="AnonymousBackInStockModal"
      forceDOMRewrite={true}
      withScrollBar={false}
      focusOnClose={focusOnCloseElement}
    >
      <div className={styles.container}>
        {!emailIsRegistered ? (
          <>
            <h2 id="modalTitle" className={styles.title}>
              {signInTitleCopy}
            </h2>
            <p className={styles.intro}>{signInTextCopy}</p>
            <div className={styles.inputWrapper}>
              <input
                ref={emailInputRef}
                name="email"
                type="email"
                className={styles.emailInput}
                placeholder={getTranslation(
                  "anonymous_back_in_stock_modal_email_form"
                )}
                aria-label={getTranslation(
                  "anonymous_back_in_stock_modal_email_form"
                )}
                aria-invalid={emailError}
                aria-describedby={
                  emailError ? "anon-bis-email-error" : undefined
                }
                value={email}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                onBlur={() => email && setEmailError(!validateEmail(email))}
              />
            </div>
            {emailError && (
              <p
                id="anon-bis-email-error"
                className={styles.emailValidationError}
                role="alert"
              >
                {getTranslation("anonymous_back_in_stock_modal_email_error")}
              </p>
            )}
            <Button
              handleClick={handleNotifyClick}
              type={BUTTON_TYPES.primary}
              additionalClasses={styles.button}
              disabled={loading}
              ariaLabel={notifyMeButtonCopy}
            >
              {loading ? (
                <ProgressIndicator size={SIZE_SMALL} aria-hidden="true" />
              ) : (
                notifyMeButtonCopy
              )}
            </Button>
            <div
              className={styles.consentWarning}
              data-testid="anon-bis-policy"
            >
              {replaceJSX(
                getTranslation("anonymous_back_in_stock_modal_terms_link"),
                ["%{privacypolicylink}", "%{termslink}"],
                [
                  <a
                    key="privacypolicylink"
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getTranslation(
                      "anonymous_back_in_stock_modal_policy_link_text"
                    )}
                  </a>,
                  <a
                    key="termslink"
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {getTranslation(
                      "anonymous_back_in_stock_modal_terms_link_text"
                    )}
                  </a>,
                ]
              )}
            </div>
          </>
        ) : (
          <AnonymousBackInStockRecognisedCustomer
            recognisedHeadingRef={recognisedHeadingRef}
            selectedVariantId={modalParameters?.selectedVariantId}
            onSignIn={backInStockSignIn}
            onUseDifferentEmail={() => setEmailIsRegistered(false)}
          />
        )}
      </div>
    </Modal>
  );
};

export default AnonymousBackInStockModal;
